import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ScriptsDTO } from '@src/services/tools/modules/v1/scripts/dto/brain-scripts.getById.response.dto';
import { GetActionsSummaryResponseDTO } from '@src/services/tools/modules/v1/scripts/dto/tools-requests.dto';
import { getScriptById, getActionsSummaryByScriptId } from '../hooks/scripts';
import { getOrganizationsNames } from '../hooks/organizations';
import { useError } from '@src/contexts/ErrorContext';

export type ScriptRequestStatus = 'PENDING' | 'RUNNING' | 'BLOCKED' | 'FINISHED' | 'FAILED';

interface ScriptsContextValue {
  organizationNames: Record<string, string>;
  isLoadingOrganizations: boolean;

  selectedScript: ScriptsDTO | null;
  isLoadingScript: boolean;
  handleScriptClick: (script: ScriptsDTO) => void;
  handleBack: () => void;

  actionsSummary: GetActionsSummaryResponseDTO | null;
  scriptStatus: ScriptRequestStatus | undefined;
  isLoadingSummary: boolean;
  isLoadingScriptStatus: boolean;

  refreshScriptData: () => Promise<void>;
}

const ScriptsContext = createContext<ScriptsContextValue | null>(null);

export const useScriptsContext = (): ScriptsContextValue => {
  const context = useContext(ScriptsContext);
  if (!context) {
    throw new Error('useScriptsContext must be used within a ScriptsProvider');
  }
  return context;
};

export const ScriptsProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setError } = useError();

  const [organizationNames, setOrganizationNames] = useState<Record<string, string>>({});
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(true);

  const [selectedScript, setSelectedScript] = useState<ScriptsDTO | null>(null);
  const [isLoadingScript, setIsLoadingScript] = useState(false);

  const [actionsSummary, setActionsSummary] = useState<GetActionsSummaryResponseDTO | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [scriptStatus, setScriptStatus] = useState<ScriptRequestStatus | undefined>(undefined);
  const [isLoadingScriptStatus, setIsLoadingScriptStatus] = useState(false);

  const longPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPollingActiveRef = useRef(false);
  const selectedScriptIdRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      setIsLoadingOrganizations(true);
      try {
        const orgNames = await getOrganizationsNames();
        setOrganizationNames(orgNames);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setIsLoadingOrganizations(false);
      }
    };
    fetchOrganizations();
  }, []);

  const loadScriptById = useCallback(
    async (scriptId: string) => {
      setIsLoadingScript(true);
      try {
        const script = await getScriptById(scriptId);
        setSelectedScript(script);
        setScriptStatus(script.execution?.status as ScriptRequestStatus | undefined);
      } catch (error) {
        console.error('Error fetching script:', error);
        setError({
          title: 'Erro ao carregar script',
          message: error?.response?.data?.message || error?.message || 'Não foi possível carregar o script',
          statusCode: error?.response?.status,
        });
        const next = new URLSearchParams(searchParams);
        next.delete('scriptId');
        setSearchParams(next);
      } finally {
        setIsLoadingScript(false);
      }
    },
    [searchParams, setSearchParams, setError],
  );

  useEffect(() => {
    const scriptId = searchParams.get('scriptId');
    if (scriptId && !selectedScript) {
      loadScriptById(scriptId);
    }
  }, []);

  useEffect(() => {
    selectedScriptIdRef.current = selectedScript?.id ?? null;

    if (!selectedScript?.id) {
      setActionsSummary(null);
      setScriptStatus(undefined);
      return;
    }

    const fetchInitialData = async () => {
      setIsLoadingSummary(true);
      setIsLoadingScriptStatus(true);
      try {
        const [summary, scriptData] = await Promise.all([
          getActionsSummaryByScriptId(selectedScript.id),
          getScriptById(selectedScript.id),
        ]);
        setActionsSummary(summary);
        setScriptStatus(scriptData.execution?.status as ScriptRequestStatus | undefined);
      } catch (error) {
        console.error('Error fetching script data:', error);
      } finally {
        setIsLoadingSummary(false);
        setIsLoadingScriptStatus(false);
      }
    };

    fetchInitialData();
  }, [selectedScript?.id]);

  const stopLongPolling = useCallback(() => {
    isLongPollingActiveRef.current = false;
    if (longPollingTimeoutRef.current) {
      clearTimeout(longPollingTimeoutRef.current);
      longPollingTimeoutRef.current = null;
    }
  }, []);

  const startLongPolling = useCallback(() => {
    if (isLongPollingActiveRef.current) return;
    isLongPollingActiveRef.current = true;

    const poll = async () => {
      const scriptId = selectedScriptIdRef.current;
      if (!isLongPollingActiveRef.current || !scriptId) return;

      try {
        const [summary, scriptData] = await Promise.all([
          getActionsSummaryByScriptId(scriptId),
          getScriptById(scriptId),
        ]);
        setActionsSummary(summary);
        const newStatus = scriptData.execution?.status as ScriptRequestStatus | undefined;
        setScriptStatus(newStatus);

        if (newStatus !== 'RUNNING') {
          isLongPollingActiveRef.current = false;
          return;
        }
      } catch (error) {
        console.error('Error in long polling:', error);
      } finally {
        if (isLongPollingActiveRef.current) {
          longPollingTimeoutRef.current = setTimeout(poll, 500);
        }
      }
    };

    longPollingTimeoutRef.current = setTimeout(poll, 500);
  }, []);

  useEffect(() => {
    if (scriptStatus === 'RUNNING') {
      startLongPolling();
    } else {
      stopLongPolling();
    }
    return () => stopLongPolling();
  }, [scriptStatus, startLongPolling, stopLongPolling]);

  const refreshScriptData = useCallback(async () => {
    const scriptId = selectedScriptIdRef.current;
    if (!scriptId) return;
    try {
      const [summary, scriptData] = await Promise.all([getActionsSummaryByScriptId(scriptId), getScriptById(scriptId)]);
      setActionsSummary(summary);
      setScriptStatus(scriptData.execution?.status as ScriptRequestStatus | undefined);
    } catch (error) {
      console.error('Error refreshing script data:', error);
    }
  }, []);

  const handleScriptClick = useCallback(
    async (script: ScriptsDTO) => {
      stopLongPolling();
      setSelectedScript(null);
      setScriptStatus(undefined);
      setActionsSummary(null);

      const next = new URLSearchParams(searchParams);
      next.set('scriptId', script.id);
      setSearchParams(next);

      // Fetch full script data from backend
      await loadScriptById(script.id);
    },
    [searchParams, setSearchParams, stopLongPolling, loadScriptById],
  );

  const handleBack = useCallback(() => {
    stopLongPolling();
    setSelectedScript(null);
    setScriptStatus(undefined);
    setActionsSummary(null);
    const next = new URLSearchParams(searchParams);
    next.delete('scriptId');
    setSearchParams(next);
  }, [searchParams, setSearchParams, stopLongPolling]);

  useEffect(() => {
    return () => stopLongPolling();
  }, [stopLongPolling]);

  const value = useMemo<ScriptsContextValue>(
    () => ({
      organizationNames,
      isLoadingOrganizations,
      selectedScript,
      isLoadingScript,
      handleScriptClick,
      handleBack,
      actionsSummary,
      scriptStatus,
      isLoadingSummary,
      isLoadingScriptStatus,
      refreshScriptData,
    }),
    [
      organizationNames,
      isLoadingOrganizations,
      selectedScript,
      isLoadingScript,
      handleScriptClick,
      handleBack,
      actionsSummary,
      scriptStatus,
      isLoadingSummary,
      isLoadingScriptStatus,
      refreshScriptData,
    ],
  );

  return <ScriptsContext.Provider value={value}>{children}</ScriptsContext.Provider>;
};
